import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Prediction } from "@/types";
import { PUBLIC_PREDICTION_COLUMNS, PREDICTIONS_READ } from "@/lib/predictionColumns";

interface LandingPredictionsArgs {
  /** ISO kickoff-time lower bound (inclusive). */
  from?: string;
  /** ISO kickoff-time upper bound (inclusive). */
  to?: string;
  /** ilike matchers against the market_type column (OR-combined). */
  marketMatchers?: string[];
  /** ilike matchers against the league column (OR-combined). */
  leagueMatchers?: string[];
  minConfidence?: number;
  sort?: "confidence" | "soonest" | "odds_desc";
  page: number;
  pageSize: number;
}

/**
 * Fetches approved, upcoming predictions for a time-based / market×time landing
 * page. Combines an optional kickoff-time window with optional market ilike
 * matching and server-side pagination + exact count.
 */
export function useLandingPredictions({
  from,
  to,
  marketMatchers = [],
  leagueMatchers = [],
  minConfidence,
  sort = "confidence",
  page,
  pageSize,
}: LandingPredictionsArgs) {
  return useQuery({
    queryKey: ["landing-predictions", from, to, marketMatchers, leagueMatchers, minConfidence, sort, page, pageSize],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      let q = supabase
        .from(PREDICTIONS_READ)
        .select(PUBLIC_PREDICTION_COLUMNS, { count: "exact" })
        .eq("status", "approved")
        .gte("kickoff_time", from ?? new Date().toISOString());

      if (to) q = q.lte("kickoff_time", to);
      if (minConfidence) q = q.gte("confidence_score", minConfidence);

      if (marketMatchers.length > 0) {
        q = q.or(marketMatchers.map((m) => `market_type.ilike.%${m}%`).join(","));
      }

      if (leagueMatchers.length > 0) {
        q = q.or(leagueMatchers.map((l) => `league.ilike.%${l}%`).join(","));
      }

      if (sort === "confidence") {
        q = q.order("confidence_score", { ascending: false }).order("kickoff_time", { ascending: true });
      } else if (sort === "odds_desc") {
        q = q.order("odds", { ascending: false });
      } else {
        q = q.order("kickoff_time", { ascending: true }).order("confidence_score", { ascending: false });
      }

      const start = page * pageSize;
      const { data, error, count } = await q.range(start, start + pageSize - 1);
      if (error) throw error;
      return { rows: ((data as unknown) as Prediction[]) || [], count: count ?? 0 };
    },
  });
}
